/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef} from 'ag-grid-community';
import {AgGridReact, CustomCellRendererProps} from 'ag-grid-react';
import classNames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {IonSpinnerTemplate} from '@ion/dls/src/grid/IonSpinnerTemplate';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getSubmissionStatusType} from './helpers';
import {CarbColumnType, CarbTabGridDataType} from './types';

interface SentinelGridProps {
  carbRowData: CarbTabGridDataType[];
  isLoading?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  carbGrid : {
    width                : '100%',
    '& .ag-root-wrapper' : {
      [theme.breakpoints.down('sm')] : {
        fontSize : '12px'
      }
    },
    '& .ag-header-statusCell-label' : {
      [theme.breakpoints.down('sm')] : {
        justifyContent : 'center'
      }
    },
    '& .ag-statusCell' : {
      [theme.breakpoints.down('sm')] : {
        padding    : '4px 8px',
        lineHeight : '1.2'
      }
    },
    '& .ag-column-hide-on-mobile' : {
      [theme.breakpoints.down('sm')] : {
        display : 'none'
      }
    },
    '& .ag-center-cols-viewport' : {
      overflowX                      : 'hidden !important',
      [theme.breakpoints.down('sm')] : {
        overflowX : 'scroll !important'
      }
    },
    '& .ag-popup' : {
      height                         : 'auto',
      [theme.breakpoints.down('sm')] : {
        maxHeight : '70vh'
      }
    },
    '&&' : {
      height                                 : '100%',
      '& .ag-body-viewport.ag-layout-normal' : {
        overflowY : 'auto'
      },
      '& .ag-overlay-no-rows-center' : {
        marginTop : 0
      }
    }
  },
  statusContainer : {
    height     : '100%',
    display    : 'flex',
    alignItems : 'center'
  },
  statusFoundation : {
    backgroundColor : dlsColor('white'),
    borderRadius    : '4px'
  },
  statusCell : {
    ...dlsAlt('typography', 'label3'),
    color           : dlsColor('grey1'),
    backgroundColor : `${dlsColor('grey3')}40`,
    fontWeight      : 500,
    fontSize        : '10px',
    lineHeight      : '16px',
    borderRadius    : '2px',
    padding         : '4px 8px',
    maxHeight       : '24px',
    minWidth        : '90px',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center'
  },
  completed : {
    color           : dlsColor('white'),
    backgroundColor : dlsColor('caGreen')
  },
  failed : {
    color           : dlsColor('white'),
    backgroundColor : dlsColor('caRed')
  },
  cancelled : {
    color           : dlsAlt('maps', 'colors', 'orange'),
    backgroundColor : `${dlsAlt('maps', 'colors', 'lightOrange')}40`
  }
}));

/**
 * A grid component for displaying CARB service details in
 * View Device Details dialog under CARB tab.
 *
 * ```typescript
 * 
 * <CarbTabGrid
 *   carbRowData={gridRowData}
 *   isLoading={isLoading}
 * />
 * 
 * ```
 */
const CarbTabGrid = ({
  carbRowData,
  isLoading = false
}: SentinelGridProps) => {
  const classes               = useStyles();
  const [gridApi, setGridApi] = useState(null);
  const path                  = 'admin-devices:view.carb.grid';
  const submissionStatus      = carbRowData[0]?.submissionStatus || '';
  const statusType            = getSubmissionStatusType(submissionStatus);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(function(api) {
    setGridApi(api);
  }, []);

  const defaultColDef: ColDef = {
    flex                     : 1,
    suppressHeaderMenuButton : true,
    sortable                 : false
  };

  // Custom statusCell Renderer (Display status statusCell)
  const StatusCell = (params: CustomCellRendererProps) => (
    <div className={classes.statusContainer}>
      <div className={classes.statusFoundation}>
        <Render.If condition={submissionStatus}>
          <div className={`${classes.statusCell} ${classes[statusType]}`}>
            {submissionStatus}
          </div>
        </Render.If>
      </div>
    </div>
  );

  /**
   * Returns column definitions for CARB grid
  */
  const getColumnDefinitions = (): ColDef[] => {
    return [
      {headerName: translate(`${path}.${CarbColumnType.VIN}`), field: 'vin'},
      {headerName: translate(`${path}.${CarbColumnType.LICENSE_PLATE}`), field: 'licensePlate'},
      {headerName: translate(`${path}.${CarbColumnType.STATE}`), field: 'state'},
      {headerName: translate(`${path}.${CarbColumnType.EMAIL_ADDRESS}`), field: 'emailAddress'},
      {headerName: translate(`${path}.${CarbColumnType.LAST_SUBMISSION}`), field: 'lastSubmission'},
      {headerName: translate(`${path}.${CarbColumnType.SUBMISSION_STATUS}`), field: 'submissionStatus', cellRenderer: StatusCell}
    ];
  };

  return (
    <div className={classNames('ag-theme-quartz', classes.carbGrid)}>
      <AgGridReact animateRows
        suppressMenuHide
        suppressPaginationPanel
        suppressColumnVirtualisation
        rowHeight={41}
        headerHeight={48}
        overlayNoRowsTemplate={translate('grids:noResults')}
        columnDefs={getColumnDefinitions()}
        defaultColDef={defaultColDef}
        loading={isLoading}
        overlayLoadingTemplate={IonSpinnerTemplate}
        onGridReady={onGridReady}
        ref={gridApi}
        rowData={carbRowData} />
    </div>
  );
};

export default React.memo(CarbTabGrid);
